import { Button, Form, Input, Typography } from 'antd';
import { Dispatch, FC, SetStateAction, useState } from 'react';

import { ModalComponent, RangeSlider } from 'componets/common';
import { Tooltip } from 'componets/ui';

import { ReactComponent as InfoIcon } from 'assets/icons/info-grey.svg';

import { FieldData, ModalProps } from 'models';
import Utils from 'utils/utils';
import { usePreventScroll, useTranslate } from 'hooks';

import styles from './index.module.scss';

export const PrimaryMarketCalculatorModal: FC<ModalProps> = ({ isOpen, onClose }) => {
	const t = useTranslate([
		'expectedReturnCalculatorText',
		'investedAmountText',
		'periodText',
		'interestRateAnnualText',
		'expectedReturnText',
		'expectedReturnTooltipText',
		'startEarningText',
		'monthsText',
	]);

	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);

	const investedAmount = Form.useWatch('investedAmount', form);
	const period = Form.useWatch('period', form);
	const annualInterestRate = Form.useWatch('annualInterestRate', form);

	const value = ((+investedAmount * +annualInterestRate) / 100 / 12) * +period;

	return (
		<ModalComponent
			isOpen={isOpen}
			onClose={onClose}
			title={t.expectedReturnCalculatorText}
			className={styles.root}
			width="456px"
		>
			<div className="modal-content">
				<Form
					form={form}
					onFieldsChange={setFields}
					fields={fields}
					initialValues={{ investedAmount: 10000, period: 12, annualInterestRate: 13 }}
				>
					<RangeRow
						title={t.investedAmountText}
						setFields={setFields}
						min={100}
						max={100000}
						name="investedAmount"
						value={investedAmount}
						suffix="€"
						isMoney
					/>

					<RangeRow
						title={t.periodText}
						setFields={setFields}
						min={3}
						max={24}
						step={1}
						name="period"
						value={period}
						suffix={t.monthsText}
					/>

					<RangeRow
						title={t.interestRateAnnualText}
						setFields={setFields}
						min={7}
						max={15}
						step={1}
						name="annualInterestRate"
						value={annualInterestRate}
						suffix="%"
						isPercent
					/>
				</Form>

				<div className={styles.row}>
					<span className={styles.expectedReturn}>
						{t.expectedReturnText}
						<Tooltip title={t.expectedReturnTooltipText}>
							<span className={styles.infoIcon}>
								<InfoIcon />
							</span>
						</Tooltip>
					</span>
					<span className={styles.amount}>€ {Math.trunc(value)}</span>
				</div>

				<Button type="primary" className={styles.button} onClick={onClose}>
					{t.startEarningText}
				</Button>
			</div>
		</ModalComponent>
	);
};

interface RangeRowProps {
	title: string;
	setFields: Dispatch<SetStateAction<FieldData[]>>;
	min: number;
	max: number;
	name: string;
	value: number;
	step?: number;
	isMoney?: boolean;
	suffix: string;
	isPercent?: boolean;
}
const RangeRow: FC<RangeRowProps> = ({
	title,
	setFields,
	min,
	max,
	name,
	value,
	step,
	isMoney,
	suffix,
	isPercent,
}) => {
	usePreventScroll();
	return (
		<div className={styles.range}>
			<div className={styles.row}>
				<span className={styles.title}>{title}</span>
				<div className={styles.value}>
					<RangeTextField
						name={name}
						setFields={setFields}
						minPrice={min}
						maxPrice={max}
						isMoney={isMoney}
						isPercent={isPercent}
					/>
					<Typography.Text type="secondary">{suffix}</Typography.Text>
				</div>
			</div>
			<RangeSlider
				isMoney={isMoney}
				suffix={suffix}
				step={step}
				isCalculator
				value={value}
				min={min}
				max={max}
				onChange={(data: number) => {
					const value = isMoney ? data.toFixed(2) : data;
					setFields([{ name: [name], value }]);
				}}
				defaultValue={value}
			/>
		</div>
	);
};

const RangeTextField: FC<{
	name: string;
	setFields: Dispatch<SetStateAction<FieldData[]>>;
	minPrice: number;
	maxPrice: number;
	isMoney?: boolean;
	isPercent?: boolean;
}> = ({ name, setFields, minPrice, maxPrice, isMoney, isPercent }) => {
	return (
		<Form.Item
			name={name}
			className={styles.input}
			normalize={isMoney ? (v) => Utils.transformToFixedNumber(v) : undefined}
		>
			<Input
				onChange={(e) => {
					const value =
						+e.target.value > maxPrice
							? maxPrice
							: isMoney
							? Utils.transformToFixedNumber(e.target.value)
							: isPercent
							? Utils.transformToFixedNumber(e.target.value, 1)
							: e.target.value?.replace(/[^0-9]/g, '');
					setFields([
						{
							name: [name],
							value,
						},
					]);
				}}
				onBlur={(e) => {
					setFields([
						{
							name: [name],
							value: isMoney
								? Utils.removeFractionalPartIfZeroes(
										Utils.transformToRangeNumber(+e.target.value, minPrice, maxPrice).toFixed(2),
								  )
								: isPercent
								? Utils.removeFractionalPartIfZeroes(
										Utils.transformToRangeNumber(+e.target.value, minPrice, maxPrice).toFixed(1),
								  )
								: Utils.transformToRangeNumber(+e.target.value, minPrice, maxPrice),
						},
					]);
				}}
			/>
		</Form.Item>
	);
};
